import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11 } from "styled-components";
import React from "react";
import { m } from "framer-motion";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { Asset } from "~/features/craft/asset";
import { takeFirst } from "~/features/app/utils";
import { type Theme, type Mode, getThemeStyle } from "~/features/ui/get-theme-style";
import { mapRange } from "~/features/app/utils";
const fragment = graphql(`
  fragment ChipEvolutionGraphItem on blocks_chipEvolutionGraphItem_BlockType {
    id
    image: baseImage {
      ...Asset
    }
    name: baseText
    year: baseText3
    range: baseNumber
    pointsPerSecond: baseNumber2
  }
`);
type Props = FragmentType<typeof fragment> & {
  theme: Theme;
  mode: Mode;
  animate: boolean;
  index: number;
  maxValue: number;
  showXAxisData: boolean;
};
export type ChipEvolutionGraphItemViewProps = {
  id?: string | number | null;
  media?: React.ReactNode;
  name?: string | null;
  year?: string | null;
  range?: number | null;
  pointsPerSecond?: number | null;
  theme: Theme;
  mode: Mode;
  animate: boolean;
  index: number;
  maxValue: number;
  showXAxisData: boolean;
};
export function ChipEvolutionGraphItemView({
  id,
  media,
  name,
  year,
  range,
  pointsPerSecond,
  theme,
  mode,
  animate,
  index,
  maxValue,
  showXAxisData
}: ChipEvolutionGraphItemViewProps) {
  const value = (range ?? 0) * (pointsPerSecond ?? 0);
  const themeStyle = getThemeStyle({
    theme,
    mode
  });
  const normalizedValue = mapRange(value, [0, maxValue], [0, 90]);
  return <_StyledDiv $_css={[{
    "position": "relative",
    "display": "flex",
    "width": "100%",
    "minWidth": "180px",
    "flexDirection": "column",
    "rowGap": "2.4rem",
    "borderBottomWidth": "1px",
    "borderRightWidth": "1px",
    "padding": "0.8rem",
    "&:last-child": {
      "borderRightWidth": "0px"
    },
    "@media (min-width: 1024px)": {
      "borderRightWidth": "1px !important",
      "&:first-child": {
        "borderLeftWidth": "1px"
      }
    }
  }, themeStyle.border]}>
      <_StyledDiv2 style={{
      bottom: `${normalizedValue}%`,
      background: `linear-gradient(180deg, ${mode === "dark" ? "#000704" : "#E3E3F0"} 50%, rgba(0, 0, 0, 0.00) 100%)`
    }} />
      <_StyledDiv3>
        <_StyledDiv4>{media}</_StyledDiv4>
        <_StyledDiv5>
          {name && <p>{name}</p>}
          {year && <_StyledP $_css2={mode === "dark" ? {
          "--tw-text-opacity": "1",
          "color": "rgb(227 227 240 / var(--tw-text-opacity))"
        } : {
          "--tw-text-opacity": "1",
          "color": "rgb(92 92 92 / var(--tw-text-opacity))"
        }}>{year}</_StyledP>}
        </_StyledDiv5>
      </_StyledDiv3>
      <_StyledDiv6>
        <_StyledMSpan initial={{
        opacity: 0
      }} animate={{
        opacity: animate ? 1 : 0
      }} transition={{
        delay: index * 0.1
      }} id={`point-${id}`} style={{
        bottom: `${normalizedValue}%`
      }} $_css3={[{
        "position": "absolute",
        "left": "50%",
        "height": "0.8rem",
        "width": "0.8rem",
        "--tw-translate-x": "-50%",
        "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
      }, mode === "dark" ? {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))"
      } : {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(0 7 4 / var(--tw-bg-opacity))"
      }]} />
      </_StyledDiv6>
      {showXAxisData && <_StyledDiv7>
          {range && <_StyledSpan $_css4={[{
        "borderRadius": "2px",
        "padding": "0.4rem"
      }, mode === "dark" ? {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(23 23 23 / var(--tw-bg-opacity))"
      } : {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(239 239 246 / var(--tw-bg-opacity))"
      }]}>
              X{range}m range
            </_StyledSpan>}
          {pointsPerSecond && <_StyledSpan2 $_css5={[{
        "borderRadius": "2px",
        "padding": "0.4rem"
      }, mode === "dark" ? {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(23 23 23 / var(--tw-bg-opacity))"
      } : {
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(239 239 246 / var(--tw-bg-opacity))"
      }]}>
              X{pointsPerSecond} points/sec
            </_StyledSpan2>}
        </_StyledDiv7>}
    </_StyledDiv>;
}
export function ChipEvolutionGraphItem(props: Props) {
  const {
    theme,
    mode,
    animate,
    index,
    maxValue,
    showXAxisData,
    ...rest
  } = props;
  const {
    id,
    image,
    name,
    year,
    range,
    pointsPerSecond
  } = useFragment(fragment, rest);
  return <ChipEvolutionGraphItemView id={id} media={<Asset asset={takeFirst(image)} imageProps={{
    width: 100,
    fit: "cover"
  }} />} name={name} year={year} range={range} pointsPerSecond={pointsPerSecond} theme={theme} mode={mode} animate={animate} index={index} maxValue={maxValue} showXAxisData={showXAxisData} />;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-gv7rid-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-gv7rid-1"
})({
  "pointerEvents": "none",
  "position": "absolute",
  "left": "0",
  "right": "0",
  "top": "0"
});
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-gv7rid-2"
})({
  "position": "relative",
  "zIndex": "10",
  "display": "flex",
  "alignItems": "center",
  "columnGap": "1rem"
});
var _StyledDiv4 = _styled4("div").withConfig({
  componentId: "sc-sf-gv7rid-3"
})({
  "position": "relative",
  "display": "flex",
  "height": "4rem",
  "width": "4rem",
  "flexShrink": "0",
  "alignItems": "center"
});
var _StyledDiv5 = _styled5("div").withConfig({
  componentId: "sc-sf-gv7rid-4"
})({
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "0.8rem",
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.2rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.024rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.028000000000000004rem",
    "fontWeight": "400"
  }
});
var _StyledP = _styled6("p").withConfig({
  componentId: "sc-sf-gv7rid-5"
})`${p => p.$_css2}`;
var _StyledDiv6 = _styled7("div").withConfig({
  componentId: "sc-sf-gv7rid-6"
})({
  "position": "relative",
  "zIndex": "10",
  "flex": "1 1 0%"
});
var _StyledMSpan = _styled8(m.span).withConfig({
  componentId: "sc-sf-gv7rid-7"
})`${p => p.$_css3}`;
var _StyledDiv7 = _styled9("div").withConfig({
  componentId: "sc-sf-gv7rid-8"
})({
  "position": "relative",
  "zIndex": "10",
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "flex-start",
  "gap": "0.2rem",
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.2rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.024rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.028000000000000004rem",
    "fontWeight": "400"
  }
});
var _StyledSpan = _styled10("span").withConfig({
  componentId: "sc-sf-gv7rid-9"
})`${p => p.$_css4}`;
var _StyledSpan2 = /*#__PURE__*/_styled11("span").withConfig({
  displayName: "chip-evolution-graph-item___StyledSpan2",
  componentId: "sc-1188ra2-0"
})(["", ""], p => p.$_css5);